import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    CircularProgress,
    Alert,
    Snackbar
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { 
    getFlashBetMatch, 
    updateFlashBetMatchMetadata, 
    deleteFlashBetMatch, 
    addFlashBetMatchQuestion,
    updateFlashBetMatchQuestion,
    deleteFlashBetMatchQuestion,
    generateQuestionsForMatch
} from '../Api';
import { FlashBetMatchDto, UpdateMatchMetadataRequest, AddMatchQuestionRequest, UpdateMatchQuestionByIdRequest } from '../models/dtos/FlashBetMatchDto';
import FlashBetMatchCard from './FlashBetMatchCard';
import EditMatchModal from './EditMatchModal';
import EditScoreRecordsModal from './EditScoreRecordsModal';
import EditMatchEventsModal from './EditMatchEventsModal';
import MatchQuestionModal from './MatchQuestionModal';

interface ViewMatchModalProps {
    open: boolean;
    onClose: () => void;
    matchId: string | null;
}

const ViewMatchModal: React.FC<ViewMatchModalProps> = ({
    open,
    onClose,
    matchId
}) => {
    const [match, setMatch] = useState<FlashBetMatchDto | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    
    // Nested modals state
    const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
    const [scoreRecordsModalOpen, setScoreRecordsModalOpen] = useState<boolean>(false);
    const [eventsModalOpen, setEventsModalOpen] = useState<boolean>(false);
    const [questionModalOpen, setQuestionModalOpen] = useState<boolean>(false);
    const [selectedQuestion, setSelectedQuestion] = useState<{ matchId: string; questionId?: string } | null>(null);
    
    // Loading states
    const [generatingQuestions, setGeneratingQuestions] = useState<boolean>(false);
    const [copiedBetsApiId, setCopiedBetsApiId] = useState<string | null>(null);
    const [snackbarMessage, setSnackbarMessage] = useState<string>('');
    const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);

    const loadMatch = async () => {
        if (!matchId) return;
        
        setLoading(true);
        setError('');
        setMatch(null);
        
        try {
            const matchData = await getFlashBetMatch(matchId);
            if (matchData) {
                setMatch(matchData);
            } else {
                setError('Failed to load match data');
            }
        } catch (err) {
            console.error('Error loading match:', err);
            setError('Error loading match data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && matchId) {
            loadMatch();
        } else if (!open) {
            // Reset state when modal closes
            setMatch(null);
            setError('');
            setEditModalOpen(false);
            setScoreRecordsModalOpen(false);
            setEventsModalOpen(false);
            setQuestionModalOpen(false);
            setSelectedQuestion(null);
            setGeneratingQuestions(false);
            setCopiedBetsApiId(null);
            setSnackbarOpen(false);
        }
    }, [open, matchId]);

    // Match operation handlers
    const handleEdit = (match: FlashBetMatchDto) => {
        setEditModalOpen(true);
    };

    const handleEditScoreRecords = (match: FlashBetMatchDto) => {
        setScoreRecordsModalOpen(true);
    };

    const handleEditEvents = (match: FlashBetMatchDto) => {
        setEventsModalOpen(true);
    };

    const handleDelete = async (match: FlashBetMatchDto) => {
        if (window.confirm('Are you sure you want to delete this match? This action cannot be undone.')) {
            try {
                const success = await deleteFlashBetMatch(match.id);
                if (success) {
                    setSnackbarMessage('Match deleted successfully');
                    setSnackbarOpen(true);
                    // Close the view modal after successful deletion
                    setTimeout(() => {
                        onClose();
                    }, 1500);
                } else {
                    setError('Failed to delete match');
                }
            } catch (error) {
                console.error('Failed to delete match:', error);
                setError('Error deleting match');
            }
        }
    };

    // Question operation handlers
    const handleAddQuestion = (match: FlashBetMatchDto) => {
        setSelectedQuestion({ matchId: match.id });
        setQuestionModalOpen(true);
    };

    const handleEditQuestion = (match: FlashBetMatchDto, questionId: string) => {
        setSelectedQuestion({ matchId: match.id, questionId });
        setQuestionModalOpen(true);
    };

    const handleDeleteQuestion = async (matchId: string, questionId: string) => {
        try {
            const updatedMatch = await deleteFlashBetMatchQuestion(matchId, questionId);
            if (updatedMatch) {
                setMatch(updatedMatch);
                setSnackbarMessage('Question deleted successfully');
                setSnackbarOpen(true);
            } else {
                setError('Failed to delete question');
            }
        } catch (error) {
            console.error('Failed to delete question:', error);
            setError('Error deleting question');
        }
    };

    const handleGenerateQuestions = async (match: FlashBetMatchDto, promptId?: string) => {
        try {
            setGeneratingQuestions(true);
            const success = await generateQuestionsForMatch(match.id, promptId);
            
            if (success) {
                // Reload the match to get updated questions
                await loadMatch();
                setSnackbarMessage('Questions generated successfully!');
                setSnackbarOpen(true);
            } else {
                setError('Failed to generate questions');
            }
        } catch (error) {
            console.error('Failed to generate questions:', error);
            setError('Error generating questions');
        } finally {
            setGeneratingQuestions(false);
        }
    };

    const handlePreviewAndGenerate = (match: FlashBetMatchDto) => {
        // For ViewMatchModal, we'll just show a message that this feature is only available on the main page
        setSnackbarMessage('Preview & Generate is available on the main matches page');
        setSnackbarOpen(true);
    };

    const handleCopyBetsApiId = async (betsApiId: string) => {
        try {
            await navigator.clipboard.writeText(betsApiId);
            setCopiedBetsApiId(betsApiId);
            setSnackbarMessage('BetsAPI ID copied to clipboard');
            setSnackbarOpen(true);
            setTimeout(() => setCopiedBetsApiId(null), 2000);
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
            setError('Failed to copy to clipboard');
        }
    };

    // Modal handlers
    const handleSaveMatch = async (data: UpdateMatchMetadataRequest) => {
        if (!match) return;

        const updatedMatch = await updateFlashBetMatchMetadata(match.id, data);
        if (!updatedMatch) {
            throw new Error('API returned null - update failed');
        }

        setMatch(updatedMatch);
        setEditModalOpen(false);
        setSnackbarMessage('Match updated successfully');
        setSnackbarOpen(true);
    };

    const handleApplyChanges = async (data: UpdateMatchMetadataRequest) => {
        if (!match) return;

        const updatedMatch = await updateFlashBetMatchMetadata(match.id, data);
        if (!updatedMatch) {
            throw new Error('API returned null - update failed');
        }

        setMatch(updatedMatch); // Keep modal open with updated data
        setSnackbarMessage('Changes applied successfully');
        setSnackbarOpen(true);
    };

    const handleQuestionSaved = async () => {
        setQuestionModalOpen(false);
        
        // Reload the match to get updated questions
        await loadMatch();
        
        setSelectedQuestion(null);
        setSnackbarMessage('Question saved successfully');
        setSnackbarOpen(true);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { minHeight: '60vh' }
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                    Match Details
                </Typography>
                <Button
                    onClick={onClose}
                    sx={{ minWidth: 'auto' }}
                    startIcon={<Close />}
                >
                    Close
                </Button>
            </DialogTitle>
            
            <DialogContent>
                {loading && (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                        <Typography variant="body2" sx={{ ml: 2 }}>
                            Loading match data...
                        </Typography>
                    </Box>
                )}
                
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                
                {match && !loading && (
                    <Box sx={{ mt: 2 }}>
                        <FlashBetMatchCard
                            match={match}
                            onEdit={handleEdit}
                            onEditScoreRecords={handleEditScoreRecords}
                            onEditEvents={handleEditEvents}
                            onDelete={handleDelete}
                            onAddQuestion={handleAddQuestion}
                            onEditQuestion={handleEditQuestion}
                            onDeleteQuestion={handleDeleteQuestion}
                            onGenerateQuestions={handleGenerateQuestions}
                            onPreviewAndGenerate={handlePreviewAndGenerate}
                            onCopyBetsApiId={handleCopyBetsApiId}
                            onShowError={(message) => {
                                setSnackbarMessage(message);
                                setSnackbarOpen(true);
                            }}
                            generatingQuestions={generatingQuestions ? match.id : null}
                            copiedBetsApiId={copiedBetsApiId}
                        />
                    </Box>
                )}
            </DialogContent>
            
            <DialogActions>
                <Button onClick={onClose} variant="outlined">
                    Close
                </Button>
            </DialogActions>

            {/* Edit Match Modal */}
            {match && (
                <EditMatchModal
                    open={editModalOpen}
                    match={match}
                    onSave={handleSaveMatch}
                    onApply={handleApplyChanges}
                    onDelete={async (matchId: string) => {
                        await handleDelete(match);
                    }}
                    onRefetch={(updatedMatch: FlashBetMatchDto) => {
                        setMatch(updatedMatch);
                    }}
                    onClose={() => setEditModalOpen(false)}
                />
            )}

            {/* Edit Score Records Modal */}
            {match && (
                <EditScoreRecordsModal
                    open={scoreRecordsModalOpen}
                    matchId={match.id}
                    onClose={() => {
                        setScoreRecordsModalOpen(false);
                        // Reload match data to get updated score records count
                        loadMatch();
                    }}
                />
            )}

            {/* Edit Match Events Modal */}
            {match && (
                <EditMatchEventsModal
                    open={eventsModalOpen}
                    matchId={match.id}
                    match={match}
                    onClose={() => setEventsModalOpen(false)}
                    onSave={() => {
                        // Reload match data to reflect any changes
                        loadMatch();
                    }}
                    onRefetch={() => {
                        // Reload match data to reflect refetched events
                        loadMatch();
                    }}
                />
            )}

            {/* Match Question Modal */}
            {match && selectedQuestion && (
                <MatchQuestionModal
                    open={questionModalOpen}
                    matchId={selectedQuestion.matchId}
                    questionId={selectedQuestion.questionId}
                    matchEnabled={match?.enabled || false}
                    onSave={handleQuestionSaved}
                    onClose={() => {
                        setQuestionModalOpen(false);
                        setSelectedQuestion(null);
                    }}
                />
            )}

            {/* Success/Error Snackbar */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
            />
        </Dialog>
    );
};

export default ViewMatchModal;